import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Tabs,
  Tab,
  Typography,
  makeStyles,
  useTheme,
} from "@material-ui/core";
import MaterialTable from "material-table";
import { Skeleton } from "@material-ui/lab";
import moment from "moment";
import { tableIcons } from "../../tableIcons";
import { backend } from "../../../../api";

const useStyles = makeStyles((theme) => ({
  root: {
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: "5px",
    marginTop: "30px",
    padding: "20px",
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down("md")]: {
      marginTop: "30px",
    },
  },
  tabHeading: {
    fontSize: "32px",
    fontWeight: 550,
    marginBottom: "20px",
  },
  cardContainer: {
    display: "flex",
    justifyContent: "space-between",
    gap: "20px",
    marginBottom: "30px",
  },
  card: {
    flex: 1,
    padding: "20px",
    backgroundColor: "#FFECB3",
    color: theme.palette.text.primary,
    borderRadius: "12px",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
    "& h6": {
      fontSize: "20px",
      fontWeight: "bold",
      marginBottom: "10px",
    },
    "& p": {
      fontSize: "16px",
      margin: "4px 0",
    },
  },
}));

function Earnings(props) {
  const classes = useStyles();
  const theme = useTheme();
  const [walletData, setWalletData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    const fetchWalletData = async () => {
      try {
        const response = await backend.get("/wallet");
        if (response.data.success) {
          setWalletData(response.data.data);
        } else {
          setError("Failed to fetch wallet data.");
        }
      } catch (error) {
        console.error("Error fetching wallet data:", error);
        setError(error.response.data.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchWalletSummary = async () => {
      try {
        const response = await backend.get("/wallet/summary");
        if (response.data.success) {
          setSummary(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching wallet summary:", error);
      }
    };

    fetchWalletData();
    fetchWalletSummary();
  }, []);

  const renderTransactions = (transactions) => (
    <MaterialTable
      icons={tableIcons}
      columns={[
        { title: "Transaction No.", field: "transaction" },
        { title: "Date", field: "date" },
        { title: "Type", field: "type" },
        { title: "Amount", field: "amount" },
        { title: "Account", field: "account" },
      ]}
      options={{
        toolbar: false,
        headerStyle: {
          backgroundColor: theme.palette.primary.light,
          color: theme.palette.secondary.light,
          fontWeight: 600,
          fontSize: 16,
        },
        rowStyle: {
          alignItems: "center",
        },
      }}
      data={transactions.map((item) => ({
        transaction: item.transaction_number || "N/A",
        date: moment(item.transaction_date).format("DD MMM, YYYY HH:mm"),
        type:
          tabValue === 0
            ? item.transactionType || "N/A"
            : item.transaction_type || "N/A",
        amount: item.amount ? `$${item.amount.toFixed(2)}` : "$0.00",
        account: item.payment_receiving_account || item.selectedAccountNo || "N/A",
      }))}
    />
  );

  return (
    <div className={classes.root}>
      <Typography className={classes.tabHeading}>My Earnings</Typography>

      {summary ? (
        <div className={classes.cardContainer}>
          {summary.eMoney ? (
            <Card className={classes.card}>
              <Typography variant="h6">eMoney Summary</Typography>
              <Typography variant="body1">
                Received: ${summary.eMoney.receivedAmount}
              </Typography>
              <Typography variant="body1">
                Spent: ${summary.eMoney.spentAmount}
              </Typography>
              <Typography variant="body1">
                Balance: ${summary.eMoney.balance}
              </Typography>
            </Card>
          ) : (
            <Skeleton animation="wave" />
          )}
          <Card className={classes.card}>
            <Typography variant="h6">Bank Account Summary</Typography>
            <Typography variant="body1">
              Credit: ${summary.bankAccount.credit}
            </Typography>
            <Typography variant="body1">
              Withdrawn: ${summary.bankAccount.withdrawn}
            </Typography>
            <Typography variant="body1">
              Balance: ${summary.bankAccount.balance}
            </Typography>
          </Card>
        </div>
      ) : loading ? (
        <Skeleton animation="wave" />
      ) : (
        <Typography color="error">{error}</Typography>
      )}

      <Box mt={4}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          centered
        >
          <Tab label="Bank Account" />
          <Tab label="eMoney" />
        </Tabs>
        <Box mt={2}>
          {loading ? (
            <Skeleton animation="wave" />
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : tabValue === 0 ? (
            renderTransactions(walletData.userWallet || [])
          ) : (
            renderTransactions(walletData.eMoneyTransactions || [])
          )}
        </Box>
      </Box>
    </div>
  );
}

export default Earnings;
